import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/shared/ui/sheet";
import { useClientId } from "@/shared/hooks/useClientId";
import { getAuditRules } from "./api";

interface RuleCatalogDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RuleCatalogDrawer({ open, onOpenChange }: RuleCatalogDrawerProps) {
  const { clientId } = useClientId();
  const { data, isLoading, error } = useQuery({
    queryKey: ["audit-rules", clientId],
    enabled: open && !!clientId,
    queryFn: () => getAuditRules(clientId as string),
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[480px] sm:max-w-[480px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            Audit rules{data ? ` · ${data.rule_pack} ${data.rubric_version}` : ""}
          </SheetTitle>
        </SheetHeader>
        {isLoading && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}
        {error && <p className="py-6 text-sm text-destructive">Couldn't load rules.</p>}
        <div className="mt-4 space-y-4">
          {(data?.rules ?? []).map((rule) => (
            <div key={rule.rule_id} className="rounded-lg ring-1 ring-border/30 bg-muted/15 p-3">
              <p className="text-sm font-semibold text-foreground">{rule.statement}</p>
              {rule.threshold && (
                <p className="mt-1 text-xs text-muted-foreground">Threshold: {rule.threshold}</p>
              )}
              {rule.rationale && (
                <p className="mt-1 text-xs italic text-muted-foreground">{rule.rationale}</p>
              )}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
